import { Injectable, inject } from '@angular/core';
import { AppActions } from '@navfleet/client/core/app';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { License } from '@navfleet/client/license/domain/models';
import { VesselPassageActions } from '@navfleet/client/monitoring/state/vessel-passage';
import {
    VesselScheduleActions,
    VesselScheduleApi,
    VesselScheduleSelectors,
    fromVesselSchedule,
} from '@navfleet/client/monitoring/state/vessel-schedule';
import { Vessel } from '@navfleet/client/shared/models/vessel';
import { logDebug, logError } from '@navfleet/client/shared/util/log';
import {
    VesselMethod,
    VesselSelectors,
    VesselSignalRService,
    fromVessel,
} from '@navfleet/client/vessel/domain/data-access';
import { VesselPassageState, VesselScheduleData } from '@navfleet/client/vessel/domain/models';
import { equal } from '@navfleet/shared/utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
    catchError,
    concatMap,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';

@Injectable()
export class VesselScheduleEffects {
    private readonly actions$ = inject(Actions);
    private readonly store$ = inject<Store<fromVesselSchedule.State & fromVessel.State>>(Store);
    private readonly vesselScheduleApi = inject(VesselScheduleApi);
    private readonly vesselSignalRService = inject(VesselSignalRService);
    private readonly licenseFacade = inject(LicenseFacade);

    /**
     * Effect that checks if loading of VesselSchedule is necessary before it dispatches loadVesselSchedules Actions.
     */
    public readonly initLoadOfVesselSchedules$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VesselScheduleActions.initLoadOfVesselSchedules),
            this.licenseFacade.hasLicenseFilter(License.PassageMonitoring),
            switchMap(({ force, vesselId, vesselPassageHaveBeenVerified }) => {
                if (force && !vesselId) {
                    return of(VesselScheduleActions.loadVesselSchedules({}));
                } else {
                    return this.store$.pipe(
                        select(VesselScheduleSelectors.selectIsVesselSchedulesLoadingOrLoaded(vesselId)),
                        withLatestFrom(
                            this.store$.select(
                                VesselScheduleSelectors.selectIsVesselScheduleContainingWaypoints(vesselId as number)
                            ),
                            this.store$.select(VesselScheduleSelectors.selectVesselScheduleError(vesselId))
                        ),
                        take(1),
                        concatMap(([vesselScheduleIsLoadingOrLoaded, haveWaypoints, error]) => {
                            // If force then it should proceed
                            // If the vessel id is provided
                            // There is no need to load if the VesselSchedule already have waypoints
                            // or is loading/loaded
                            if (!force && !!vesselId && (haveWaypoints || vesselScheduleIsLoadingOrLoaded)) {
                                return EMPTY;
                            }

                            // If force then it should proceed
                            // If it is already loading or loaded for all vessels then it should not load
                            // Note: If the vesselId is undefined then it is load for all vessels
                            if (!force && !vesselId && vesselScheduleIsLoadingOrLoaded) {
                                return EMPTY;
                            }

                            // If force then it should proceed even though the previous request failed
                            if (!force && error) {
                                logError('VesselScheduleEffects', 'initLoadOfVesselSchedules$', 'error', error);
                                return EMPTY;
                            }

                            if (vesselId && !vesselPassageHaveBeenVerified) {
                                return of(
                                    VesselPassageActions.initLoadOfVesselPassages({
                                        force,
                                        loadVesselSchedule: true,
                                        vesselId,
                                        vesselPassageState: VesselPassageState.Active,
                                    })
                                );
                            } else {
                                return of(VesselScheduleActions.loadVesselSchedules({ vesselId }));
                            }
                        })
                    );
                }
            })
        )
    );

    /**
     * Effect that loads VesselSchedules from the api.
     */
    public readonly loadVesselSchedules$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VesselScheduleActions.loadVesselSchedules),
            this.licenseFacade.hasLicenseFilter(License.PassageMonitoring),
            mergeMap(({ vesselId }) =>
                this.vesselScheduleApi.getVesselSchedules(vesselId).pipe(
                    take(1),
                    map((vesselSchedules) =>
                        VesselScheduleActions.loadVesselSchedulesSuccess({
                            vesselId,
                            vesselSchedules,
                        })
                    ),
                    catchError((error) =>
                        of(
                            VesselScheduleActions.loadVesselSchedulesFailure({
                                error,
                                vesselId,
                            })
                        )
                    ),
                    takeUntil(this.actions$.pipe(ofType(AppActions.applicationClosed)))
                )
            )
        )
    );

    public readonly listenToSignalRVesselScheduleCalculated$ = createEffect(
        () =>
            this.licenseFacade
                .hasLicense(License.PassageMonitoring)
                .pipe(
                    switchMap(() =>
                        this.vesselSignalRService.on$(VesselMethod.ScheduleCalculated).pipe(tap((event) => this.scheduleCalculated(event)))
                    )
                ),
        { dispatch: false }
    );

    public readonly listenToSignalRVesselScheduleInvalidated$ = createEffect(
        () =>
            this.licenseFacade
                .hasLicense(License.PassageMonitoring)
                .pipe(
                    switchMap(() =>
                        this.vesselSignalRService.on$(VesselMethod.ScheduleInvalidated).pipe(tap((event) => this.scheduleInvalidated(event)))
                    )
                ),
        { dispatch: false }
    );

    private readonly vesselIds$: Observable<number[]> = this.store$.select(VesselSelectors.selectNavFleetVessels).pipe(
        filter((vessels) => !!vessels.length),
        map((vessels) => vessels.map((v) => v.id)),
        distinctUntilChanged(equal),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    private scheduleCalculated(scheduleData: VesselScheduleData): void {
        this.vesselIds$.pipe(take(1)).subscribe({
            next: (vesselIds) => {
                const vesselId = scheduleData.id;
                const hasVessel = vesselIds.includes(vesselId);

                logDebug(
                    'SignalR',
                    'VesselScheduleEffects',
                    'scheduleCalculated',
                    'vesselId',
                    scheduleData?.id,
                    'VesselScheduleData Matches vesselId',
                    hasVessel,
                    'VesselScheduleData',
                    scheduleData
                );

                if (!hasVessel) {
                    return;
                }

                const vesselSchedule = scheduleData.convertToVesselSchedule();

                // Dispatch to load new data
                this.store$.dispatch(
                    VesselScheduleActions.signalRLoadedScheduleSuccess({
                        vesselSchedule,
                    })
                );
            },
        });
    }

    private scheduleInvalidated(vesselId: number): void {
        this.vesselIds$.pipe(take(1)).subscribe({
            next: (vesselIds) => {
                const hasVessel = vesselIds.includes(vesselId);

                logDebug(
                    'SignalR',
                    'VesselScheduleEffects',
                    'scheduleInvalidated',
                    'vesselId',
                    vesselId,
                    'ScheduleInvalidated matches vesselId',
                    hasVessel
                );

                if (!hasVessel) {
                    return;
                }

                if (Vessel.idIsValid(vesselId)) {
                    // Dispatch to clear invalidated schedule from store
                    this.store$.dispatch(
                        VesselScheduleActions.clearVesselSchedule({
                            vesselId,
                        })
                    );
                }
            },
        });
    }
}
